import AppForm from '@/components/AppForm';
import useBlockNavigation from '@/components/AppForm/useBlockNavigation';
import AppLoading from '@/components/AppLoading';
import AppPageTitle from '@/components/AppPageTitle';
import AppSelect from '@/components/AppSelect';
import PATH from '@/config/paths/PATH';
import { useUnsaved } from '@/contexts/UnsavedContext';
import useFormSubmit from '@/hooks/useFormSubmit';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import usePrizeManagement from '@/redux/hooks/usePrizeManagement';
import { deletePieceList, getPieceList } from '@/services/piece.service';
import { editPrizeManagement } from '@/services/prize_management.service';
import { columnSorter, formatNumber } from '@/utils/formatters';
import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Switch,
  Tag,
} from 'antd';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import FormUpload from '@/components/common/FormUpload';
import RowAction from './RowAction';
import { toast } from 'react-toastify';
import PieceTable from './PieceTable';
import { CloseOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

function PrizeManagementUpdate() {
  const {
    prizeManagement: { prize },
    clearPrizeValue,
    setPieceValue,
  } = usePrizeManagement();
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [pieceListNew, setPieceListNew] = useState([]);
  const [pieceList, setPieceList] = useState([]);
  const [isEditNewPiece, setIsEditNewPiece] = useState(false);
  const [hasUserChanges, setHasUserChanges] = useState(false);
  const { hasUnsaved, setHasUnsaved } = useUnsaved();
  const location = useLocation();
  const { blockNavigation } = useBlockNavigation(
    hasUserChanges || pieceListNew.length > 0,
    'You have unsaved changes. Are you sure you want to leave?',
    () => {
      setHasUnsaved(false);
      setHasUserChanges(false);
      setPieceListNew([]);
    }
  );

  const returnUrl = location.state?.returnUrl;

  const navigate = useNavigate();

  const [form] = Form.useForm();

  const {
    data: dataPieceList,
    isLoading,
    isRefetching,
    refetch,
  } = usePaginatedQuery(() => getPieceList({ prizeId: prize?.id }), ['pieceList', prize?.id], {
    enabled: !!prize?.id,
  });

  const prizeMutation = useMutation({
    mutationFn: editPrizeManagement,
  });

  const { mutate: deletePiece } = useMutation({
    mutationFn: deletePieceList,
  });

  const handleOnSubmit = useFormSubmit(
    prizeMutation,
    async (values) => {
      const imageFile = values.image?.[0]?.originFileObj || null;
      const { image, isdn_win, isdn_block, ...restValues } = values;
      const piecesData = pieceList
        ?.filter((item) => item.is_new)
        ?.map((item) => {
          const { image_file, ...pieceData } = item;
          const originalFileName = image_file?.name || 'unnamed';

          const fileExtension = originalFileName.includes('.')
            ? originalFileName.split('.').pop()
            : 'jpg';
          const baseFileName = originalFileName.includes('.')
            ? originalFileName.replace(/\.[^/.]+$/, '')
            : originalFileName;
          const newFileName = `${baseFileName}_piece_${item.id}.${fileExtension}`;
          return {
            ...pieceData,
            is_active: item.is_active ? 1 : 0,
            is_limited: item.is_limited ? 1 : 0,
            piece_image_id: newFileName,
          };
        });

      const payload = {
        ...restValues,
        is_active: values.is_active ? 1 : 2,
        id: prize.id,
        list_pieces: piecesData,
        isdn_win: isdn_win ? isdn_win.replace(/\s+/g, '') : '',
        isdn_block: isdn_block ? isdn_block.replace(/\s+/g, '') : '',
      };

      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        if (typeof payload[key] === 'object') {
          formData.append(key, JSON.stringify(payload[key]));
        } else {
          formData.append(key, payload[key]);
        }
      });

      if (imageFile) {
        formData.append('image', imageFile);
      }

      pieceList
        ?.filter((item) => item.is_new)
        ?.forEach((item, index) => {
          if (item.image_file) {
            const originalFileName = item.image_file?.name || 'unnamed';

            const fileExtension = originalFileName.includes('.')
              ? originalFileName.split('.').pop()
              : 'jpg';
            const baseFileName = originalFileName.includes('.')
              ? originalFileName.replace(/\.[^/.]+$/, '')
              : originalFileName;

            const newFileName = `${baseFileName}_piece_${item.id}.${fileExtension}`;

            formData.append('piece_image', item.image_file, newFileName);
          }
        });

      return formData;
    },
    {
      onOk: async () => {
        // Navigate back to the previous page with search params preserved
        const targetUrl = returnUrl || PATH.PRIZE_MANAGEMENT.ROOT;
        await navigate(targetUrl, {
          replace: true,
        });
        clearPrizeValue();
      },
    }
  );

  const columns = useMemo(
    () => [
      {
        title: 'No.',
        dataIndex: 'id',
        key: 'id',
        align: 'center',
        sorter: columnSorter('id'),
        render: (id, record, index) =>
          record.is_new ? <Badge status="success" text="New"></Badge> : <span>{id}</span>,
      },
      {
        title: 'Piece Name',
        dataIndex: 'name',
        key: 'piece_name',
        sorter: columnSorter('name'),
      },
      {
        title: 'Ordinal',
        dataIndex: 'ordinal_number',
        key: 'ordinal_number',
        sorter: columnSorter('name'),
      },
      {
        title: 'Drop Rate',
        dataIndex: 'percent',
        key: 'percent',
        render: (percent) => (percent ? `${percent}%` : '0%'),
        sorter: columnSorter('percent'),
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity_piece',
        key: 'quantity_piece',
        sorter: columnSorter('quantity_piece'),
        render: (quantity) => (quantity ? formatNumber(quantity) : '0'),
      },
      {
        title: 'Limited',
        dataIndex: 'is_limited',
        key: 'is_limited',
        sorter: columnSorter('is_limited'),
        render: (isLimited) => (isLimited ? 'Yes' : 'No'),
      },
      {
        title: 'Active',
        dataIndex: 'is_active',
        key: 'is_active',
        render: (isActive) => (isActive ? 'Yes' : 'No'),
        sorter: columnSorter('is_active'),
      },
      {
        title: 'Image',
        dataIndex: 'image_path',
        key: 'image',
        render: (imagePath, record, index) => {
          let image;
          if (record?.image_file) {
            image = URL.createObjectURL(record?.image_file);
          } else {
            image = `${process.env.REACT_APP_API_BASEURL}${imagePath}`;
          }
          return (
            <div className="w-full flex justify-center">
              <img src={image} alt="Piece" className="w-[50px] h-[50px] object-cover" />
            </div>
          );
        },
      },
    ],
    []
  );

  const initialValues = useMemo(() => {
    if (!prize) return {};
    let imageList = [];
    if (prize.image_path) {
      imageList = [
        {
          uid: '-1',
          name: `Prize ${prize.name}`,
          status: 'done',
          url: `${process.env.REACT_APP_API_BASEURL}${prize.image_path}`,
        },
      ];
    }
    return {
      ...prize,
      is_active: prize?.is_active === 1 ? true : false,
      image: imageList,
    };
  }, [prize]);

  const processedPieceList = useMemo(() => {
    const transformItem = (item, isNew = false) => ({
      ...item,
      key: item.id,
      name: item.name || item.piece_code,
      ordinal_number: item.ordinal_number || 0,
      percent: item.percent || 0,
      quantity_piece: item.quantity_piece || 0,
      is_active: item.is_active ? 1 : 0,
      is_limited: item.is_limited ? 1 : 0,
      image_path: item.image_path || (isNew ? '/image-path' : item.image_path),
      is_new: isNew,
    });

    if (pieceListNew.length > 0 && Array.isArray(dataPieceList)) {
      const newPieces = pieceListNew.map((item) => transformItem(item, true));
      const existingPieces = dataPieceList.map((item) => transformItem(item, false));
      return [...newPieces, ...existingPieces];
    }

    return dataPieceList?.map((item) => transformItem(item, false)) || [];
  }, [dataPieceList, pieceListNew]);

  useEffect(() => {
    setPieceList(processedPieceList);
  }, [processedPieceList]);

  useEffect(() => {
    if (pieceListNew.length > 0) {
      setHasUnsaved(true);
      setHasUserChanges(true);
    } else {
      if (pieceListNew.length === 0) {
        setHasUserChanges(false);
      }
    }
  }, [pieceListNew, setHasUnsaved]);

  const handleFormValuesChange = (changedValues, allValues) => {
    const userChangedFields = Object.keys(changedValues);

    if (userChangedFields.length > 0) {
      setHasUserChanges(true);
      setHasUnsaved(true);
    }
  };

  useEffect(() => {
    if (prize && Object.keys(initialValues).length > 0) {
      form.resetFields();
      form.setFieldsValue(initialValues);
      setHasUserChanges(false);
    }
  }, [prize, initialValues, form]);

  const handleRemovePiece = (item) => {
    Modal.confirm({
      title: 'Delete piece',
      content: `Are you sure you want to delete the piece "${item.name}"?`,
      onOk: () => {
        if (item.is_new) {
          if (pieceListNew.length === 1) {
            setPieceListNew([]);
            return;
          }
          const filtered = pieceListNew.filter((i) => i.id !== item.id);
          setPieceListNew(filtered);
          toast.success('Success!');
        } else {
          deletePiece({id: item.id, prize_id: prize.id}, {
            onSuccess: (data) => {
              toast.success('Success!');
              refetch();
            },
            onError: (error) => {
              const errorMessage = error?.data?.errorMessage || 'Something went wrong';
              toast.error(errorMessage, {
                autoClose: 3000,
                hideProgressBar: true,
              });
            },
          });
        }
      },
    });
  };

  const handleOnCancel = () => {
    if ((form.isFieldsTouched() && hasUnsaved) || pieceListNew.length > 0) {
      blockNavigation(returnUrl || PATH.PRIZE_MANAGEMENT.ROOT);
    } else {
      navigate(returnUrl || PATH.PRIZE_MANAGEMENT.ROOT, { replace: true });
    }
  };

  const isdnWinValue = Form.useWatch('isdn_win', form);
  const isdnBlockValue = Form.useWatch('isdn_block', form);

  const phoneNumberWins = useMemo(() => {
    if (!isdnWinValue) return [];
    return isdnWinValue
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num.length > 0);
  }, [isdnWinValue]);

  const phoneNumberBlocks = useMemo(() => {
    if (!isdnBlockValue) return [];
    return isdnBlockValue
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num.length > 0);
  }, [isdnBlockValue]);

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  return (
    <Suspense fallback={<AppLoading />}>
      <Space direction="vertical" className="w-full">
        <AppPageTitle title="Update Prize" />

        <AppForm
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          form={form}
          targetPath={returnUrl || PATH.PRIZE_MANAGEMENT.ROOT}
          onValuesChange={handleFormValuesChange}
          buttonProps={{
            disabled: prizeMutation.isPending,
            loading: prizeMutation.isPending,
            saveDisabled: !hasUserChanges && pieceListNew.length === 0,
          }}
          onCancel={handleOnCancel}
          disabled={prizeMutation.isPending}
        >
          <Row gutter={24}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                label="Prize Name"
                name="name"
                rules={[{ message: 'Please prize name', required: true }]}
              >
                <Input placeholder="Enter prize name" />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[{ message: 'Please enter prize description', required: true }]}
              >
                <Input placeholder="Enter prize description" />
              </Form.Item>
              <Form.Item
                label="Price (MMK)"
                name="price"
                rules={[{ message: 'Please enter price', required: true }]}
              >
                <InputNumber
                  placeholder="Enter price"
                  min={0}
                  formatter={(value) => formatNumber(value)}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  className="w-full"
                />
              </Form.Item>

              <Form.Item
                label="Period"
                name="period"
                rules={[{ message: 'Please enter period', required: true }]}
              >
                <Row gutter={8} className="flex-nowrap justify-between">
                  <Col span={11}>
                    <Form.Item
                      name="start_date"
                      noStyle
                      getValueFromEvent={(value) => value?.format('YYYY-MM-DD')}
                      getValueProps={(value) => ({
                        value: value ? dayjs(value) : undefined,
                      })}
                      rules={[
                        {
                          required: true,
                          message: 'Please select start date',
                        },
                      ]}
                    >
                      <DatePicker
                        placeholder="Start Date"
                        format="YYYY-MM-DD"
                        className="w-full"
                        onChange={() => {
                          form.validateFields(['end_date']);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col className="text-center flex items-center">
                    <span className="text-gray-500">to</span>
                  </Col>
                  <Col span={11}>
                    <Form.Item
                      name="end_date"
                      noStyle
                      getValueFromEvent={(value) => value?.format('YYYY-MM-DD')}
                      getValueProps={(value) => ({
                        value: value ? dayjs(value) : undefined,
                      })}
                      rules={[
                        { required: true, message: 'Please select end date' },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();

                            const startDate = form.getFieldValue('start_date');
                            if (!startDate) return Promise.resolve();

                            const start = dayjs(startDate);
                            const end = dayjs(value);

                            if (end.isBefore(start, 'd')) {
                              return Promise.reject(
                                new Error('End date cannot be before start date')
                              );
                            }

                            if (end.isSame(start, 'd')) {
                              return Promise.reject(new Error('End date must be after start date'));
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <DatePicker
                        placeholder="End Date"
                        format="YYYY-MM-DD"
                        className="w-full"
                        disabledDate={(current) => {
                          const startDate = form.getFieldValue('start_date');
                          const currentDate = dayjs(current);
                          if (!startDate) return false;

                          return (
                            current &&
                            (currentDate.isSame(dayjs(startDate), 'd') ||
                              currentDate.isBefore(dayjs(startDate), 'd'))
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item
                label="Image"
                name="image"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
                rules={[{ message: 'Please upload image', required: true }]}
              >
                <FormUpload className="w-[300px]" beforeUpload={() => false} />
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                label="Prize Number"
                name="num_prize"
                rules={[{ message: 'Please enter prize number', required: true }]}
              >
                <InputNumber
                  className="w-full"
                  placeholder="Enter prize number"
                  min={0}
                  formatter={(value) => formatNumber(value)}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
              <Form.Item
                label="Prize Type"
                name="type"
                rules={[{ message: 'Please select prize type', required: true }]}
                initialValue={1}
              >
                <AppSelect
                  items={[
                    { value: 1, label: 'Topup' },
                    { value: 2, label: 'Physical' },
                  ]}
                  placeholder="Select prize type"
                  formItemClassName="mb-0"
                />
              </Form.Item>
              <Form.Item
                label="Percentage"
                name="percent"
                rules={[{ message: 'Please enter percentage', required: true }]}
              >
                <Input
                  type="number"
                  addonBefore="%"
                  className="w-full"
                  placeholder="Enter percentage"
                  min={0}
                  max={100}
                />
              </Form.Item>
              <Form.Item
                label="Piece Per Row"
                name="piece_per_row"
                rules={[{ message: 'Please enter piece per row', required: true }]}
              >
                <Input min={1} type="number" placeholder="Enter piece per row" />
              </Form.Item>
              <Form.Item
                label="ISDN Win"
                name="isdn_win"
                rules={[
                  // { message: 'Please enter ISDN Win', required: true },
                  {
                    pattern: /^(\+?\d+(,\s*\+?\d+)*)?$/,
                    message: 'Only numbers separated by commas (with optional spaces) are allowed',
                  },
                ]}
              >
                <Input
                  placeholder="Enter ISDN Win"
                  type="tel"
                  onBeforeInput={(e) => {
                    // allow only digits, +, comma, and space
                    if (!/[0-9,+ ]/.test(e.data)) {
                      e.preventDefault();
                    }
                  }}
                  onKeyDown={(e) => {
                    // allow navigation & meta shortcuts
                    if (
                      e.ctrlKey ||
                      e.metaKey ||
                      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                    ) {
                      return;
                    }

                    // block other unwanted keys
                    if (!/[0-9,+ ]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
              {phoneNumberWins?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {phoneNumberWins?.map((num, idx) => (
                    <Tag
                      key={`${num}-${uuidv4()}`} // Stable key: use the phone number itself (unique)
                      closable
                      onClose={() => {
                        const newValue = phoneNumberWins
                          ?.filter((_, i) => i !== idx) // Filter by index (stable with good keys)
                          ?.join(', ');
                        form.setFieldValue('isdn_win', newValue);
                        setHasUnsaved(true);
                        setHasUserChanges(true);
                      }}
                      closeIcon={<CloseOutlined />}
                      color="blue"
                    >
                      {num}
                    </Tag>
                  ))}
                </div>
              )}
              <Form.Item
                label="ISDN Block"
                name="isdn_block"
                rules={[
                  // { message: 'Please enter ISDN Block', required: true },
                  {
                    pattern: /^(\+?\d+(,\s*\+?\d+)*)?$/,
                    message: 'Only numbers separated by commas (with optional spaces) are allowed',
                  },
                ]}
              >
                <Input
                  placeholder="Enter ISDN Block"
                  type="tel"
                  onBeforeInput={(e) => {
                    // allow only digits, +, comma, and space
                    if (!/[0-9,+ ]/.test(e.data)) {
                      e.preventDefault();
                    }
                  }}
                  onKeyDown={(e) => {
                    // allow navigation & meta shortcuts
                    if (
                      e.ctrlKey ||
                      e.metaKey ||
                      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                    ) {
                      return;
                    }

                    // block other unwanted keys
                    if (!/[0-9,+ ]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
              {phoneNumberBlocks?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {phoneNumberBlocks?.map((num, idx) => (
                    <Tag
                      key={`${num}-${uuidv4()}`} // Stable key: use the phone number itself (unique)
                      closable
                      onClose={() => {
                        const newValue = phoneNumberBlocks
                          ?.filter((_, i) => i !== idx) // Filter by index (stable with good keys)
                          ?.join(', ');
                        form.setFieldValue('isdn_block', newValue);
                        setHasUnsaved(true);
                      }}
                      closeIcon={<CloseOutlined />}
                      color="blue"
                    >
                      {num}
                    </Tag>
                  ))}
                </div>
              )}
              <Form.Item label="Active" name="is_active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <PieceTable
            columns={columns}
            setPieceList={setPieceListNew}
            onOpenCreate={() => setOpenCreate(true)}
            loading={isLoading || isRefetching}
            dataSource={processedPieceList}
            hasDeleteAction
            onCreate={() => {
              setOpenCreate(true);
            }}
            openCreate={openCreate}
            openUpdate={openUpdate}
            setOpenCreate={setOpenCreate}
            setOpenUpdate={setOpenUpdate}
            isNewPiece={isEditNewPiece}
            rowAction={(record) => (
              <RowAction
                record={record}
                onEdit={() => {
                  setPieceValue(record);
                  setIsEditNewPiece(record.is_new);
                  setOpenUpdate(true);
                }}
                onDelete={() => {
                  handleRemovePiece(record);
                }}
              />
            )}
          />
        </AppForm>
      </Space>
    </Suspense>
  );
}

export default PrizeManagementUpdate;
